import React, { useState } from 'react';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { parseRupiah } from '../../../utils/format';
import {
  PROCESS_TYPE_OTHER,
  PROCESS_TYPE_PRESETS,
  SPECIES,
  type Species,
} from './types';

interface CellShellProps {
  hasError: boolean;
  errorMessage?: string;
  children: React.ReactNode;
}

export const CellShell: React.FC<CellShellProps> = ({ hasError, errorMessage, children }) => (
  <div
    className={cn(
      'rounded-md ring-1 transition-colors',
      hasError
        ? 'ring-red-300 bg-red-50/50 focus-within:ring-red-500'
        : 'ring-transparent hover:ring-gray-200 focus-within:ring-primary-400 focus-within:bg-white',
    )}
    title={errorMessage}
  >
    {children}
  </div>
);

interface TextCellProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hasError: boolean;
  errorMessage?: string;
  testId: string;
}

export const TextCell: React.FC<TextCellProps> = ({
  value,
  onChange,
  placeholder,
  hasError,
  errorMessage,
  testId,
}) => (
  <CellShell hasError={hasError} errorMessage={errorMessage}>
    <input
      data-test-id={testId}
      type="text"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-transparent px-2.5 py-1.5 text-sm text-gray-900 outline-none placeholder:text-gray-400"
    />
  </CellShell>
);

interface NumberCellProps {
  value: number;
  onChange: (v: number) => void;
  hasError: boolean;
  errorMessage?: string;
  testId: string;
  step?: number;
}

export const NumberCell: React.FC<NumberCellProps> = ({
  value,
  onChange,
  hasError,
  errorMessage,
  testId,
  step = 0.01,
}) => (
  <CellShell hasError={hasError} errorMessage={errorMessage}>
    <input
      data-test-id={testId}
      type="number"
      step={step}
      value={Number.isFinite(value) ? value : 0}
      onChange={(e) => {
        const n = parseFloat(e.target.value);
        onChange(Number.isFinite(n) ? n : 0);
      }}
      className="w-full bg-transparent px-2.5 py-1.5 text-sm text-gray-900 outline-none tabular-nums"
    />
  </CellShell>
);

interface RupiahCellProps {
  value: number;
  onChange: (v: number) => void;
  hasError: boolean;
  errorMessage?: string;
  testId: string;
}

// Always-prefixed Rupiah input. The "Rp" adornment stays visible while editing
// (sits outside the input as a non-editable span). When blurred the input
// shows "150,000" with thousand separators; when focused it shows raw digits
// so cursor handling stays simple.
export const RupiahCell: React.FC<RupiahCellProps> = ({
  value,
  onChange,
  hasError,
  errorMessage,
  testId,
}) => {
  const formattedThousands = (n: number): string => {
    if (!Number.isFinite(n) || n === 0) return '';
    const parts = n.toFixed(2).split('.');
    const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const decClean = parts[1].replace(/0+$/, '');
    return decClean ? `${intPart}.${decClean}` : intPart;
  };

  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState<string>(() =>
    value === 0 ? '' : String(value),
  );

  const display = focused ? draft : formattedThousands(value);

  return (
    <CellShell hasError={hasError} errorMessage={errorMessage}>
      <div className="flex items-center gap-1 px-2.5 py-1.5">
        <span className="select-none text-sm text-gray-500">Rp</span>
        <input
          data-test-id={testId}
          type="text"
          inputMode="decimal"
          value={display}
          onFocus={() => {
            setFocused(true);
            setDraft(value === 0 ? '' : String(value));
          }}
          onChange={(e) => {
            // Keep digits, dot, and comma — strip stray "Rp" if pasted.
            const cleaned = e.target.value.replace(/[^\d.,]/g, '');
            setDraft(cleaned);
          }}
          onBlur={() => {
            // parseRupiah handles "Rp 150,000" and "150,000"; fall back to
            // parseFloat for bare decimals like "1500.50".
            const fromRp = parseRupiah(draft);
            const next = fromRp !== 0 ? fromRp : parseFloat(draft) || 0;
            onChange(Number.isFinite(next) ? next : 0);
            setFocused(false);
          }}
          className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none tabular-nums"
        />
      </div>
    </CellShell>
  );
};

interface SpeciesCellProps {
  value: string;
  onChange: (v: Species | '') => void;
  hasError: boolean;
  errorMessage?: string;
  testId: string;
}

export const SpeciesCell: React.FC<SpeciesCellProps> = ({
  value,
  onChange,
  hasError,
  errorMessage,
  testId,
}) => {
  const isValid = (SPECIES as readonly string[]).includes(value);
  return (
    <Listbox value={isValid ? value : ''} onChange={(v) => onChange(v as Species)}>
      <div className="relative">
        <CellShell hasError={hasError} errorMessage={errorMessage}>
          <ListboxButton
            data-test-id={testId}
            className="relative w-full cursor-default bg-transparent py-1.5 pl-2.5 pr-8 text-left text-sm text-gray-900 outline-none"
          >
            <span className={cn('block truncate', !isValid && 'text-gray-400')}>
              {isValid ? value : 'Select…'}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
            </span>
          </ListboxButton>
        </CellShell>
        <ListboxOptions
          anchor="bottom start"
          className="z-[60] mt-1 max-h-60 w-[var(--button-width)] min-w-[8rem] overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none"
        >
          {SPECIES.map((s) => (
            <ListboxOption
              key={s}
              value={s}
              className={({ focus }) =>
                cn(
                  'relative cursor-default select-none py-1.5 pl-8 pr-3',
                  focus ? 'bg-primary-50 text-primary-900' : 'text-gray-900',
                )
              }
            >
              {({ selected }) => (
                <>
                  <span className={cn('block truncate', selected ? 'font-medium' : 'font-normal')}>
                    {s}
                  </span>
                  {selected && (
                    <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-primary-600">
                      <Check className="h-4 w-4" aria-hidden="true" />
                    </span>
                  )}
                </>
              )}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  );
};

interface ProcessTypeCellProps {
  value: string;
  onChange: (v: string) => void;
  hasError: boolean;
  errorMessage?: string;
  testId: string;
}

type ListboxChoice = (typeof PROCESS_TYPE_PRESETS)[number] | typeof PROCESS_TYPE_OTHER;

const isPreset = (v: string): v is (typeof PROCESS_TYPE_PRESETS)[number] =>
  (PROCESS_TYPE_PRESETS as readonly string[]).includes(v);

export const ProcessTypeCell: React.FC<ProcessTypeCellProps> = ({
  value,
  onChange,
  hasError,
  errorMessage,
  testId,
}) => {
  // Local mode lets us keep "Other" selected even when the typed value is
  // momentarily empty (right after the user picks Other). It's seeded from
  // the incoming value so imported rows render in the right mode on mount.
  const [otherMode, setOtherMode] = useState<boolean>(
    !isPreset(value) && value !== '',
  );

  const selected: ListboxChoice | undefined = isPreset(value)
    ? value
    : otherMode
    ? PROCESS_TYPE_OTHER
    : undefined;

  const handleSelect = (choice: ListboxChoice) => {
    if (choice === PROCESS_TYPE_OTHER) {
      setOtherMode(true);
      // Clear so the user is forced to type — validation requires non-empty.
      if (isPreset(value)) onChange('');
    } else {
      setOtherMode(false);
      onChange(choice);
    }
  };

  const options: readonly ListboxChoice[] = [
    ...PROCESS_TYPE_PRESETS,
    PROCESS_TYPE_OTHER,
  ];

  return (
    <Listbox value={selected} onChange={handleSelect}>
      <div className="relative">
        <CellShell hasError={hasError} errorMessage={errorMessage}>
          {otherMode ? (
            // In-place: typing happens in this row; the chevron on the right
            // still opens the choice list so users can switch back to a preset.
            <div className="flex items-center">
              <input
                data-test-id={`${testId}-other-input`}
                type="text"
                value={isPreset(value) ? '' : value}
                placeholder="Type process type…"
                autoFocus={value === ''}
                onChange={(e) => {
                  const raw = e.target.value.replace(/^\s+/, '');
                  const normalized = raw
                    ? raw.charAt(0).toUpperCase() + raw.slice(1)
                    : '';
                  onChange(normalized);
                }}
                className="flex-1 min-w-0 bg-transparent px-2.5 py-1.5 text-sm text-gray-900 outline-none placeholder:text-gray-400"
              />
              <ListboxButton
                data-test-id={`${testId}-toggle`}
                aria-label="Change process type"
                className="flex h-7 w-7 items-center justify-center text-gray-400 hover:text-gray-600 outline-none"
              >
                <ChevronDown className="h-4 w-4" aria-hidden="true" />
              </ListboxButton>
            </div>
          ) : (
            <ListboxButton
              data-test-id={testId}
              className="relative w-full cursor-default bg-transparent py-1.5 pl-2.5 pr-8 text-left text-sm text-gray-900 outline-none"
            >
              <span
                className={cn(
                  'block truncate',
                  selected === undefined && 'text-gray-400',
                )}
              >
                {selected === undefined ? 'Select…' : selected}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
              </span>
            </ListboxButton>
          )}
        </CellShell>
        <ListboxOptions
          anchor="bottom start"
          className="z-[60] mt-1 max-h-60 w-[var(--button-width)] min-w-[8rem] overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none"
        >
          {options.map((opt) => (
            <ListboxOption
              key={opt}
              value={opt}
              className={({ focus }) =>
                cn(
                  'relative cursor-default select-none py-1.5 pl-8 pr-3',
                  focus ? 'bg-primary-50 text-primary-900' : 'text-gray-900',
                )
              }
            >
              {({ selected: isSelected }) => (
                <>
                  <span
                    className={cn(
                      'block truncate',
                      isSelected ? 'font-medium' : 'font-normal',
                    )}
                  >
                    {opt}
                  </span>
                  {isSelected && (
                    <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-primary-600">
                      <Check className="h-4 w-4" aria-hidden="true" />
                    </span>
                  )}
                </>
              )}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  );
};
